import type { BaseModelAttributes } from './base';
import { Locale } from './locale';
import { I18nLabels, Resolution, AspectRatio } from './common';
import { GenerationStepType } from './projectSettingGeneration';

export enum ProjectStatus {
  DRAFT = 'draft',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ARCHIVED = 'archived'
}

export const ProjectStatusLabels: I18nLabels<ProjectStatus> = {
  [ProjectStatus.DRAFT]: { [Locale.ZH_CN]: '草稿', [Locale.EN_US]: 'Draft' },
  [ProjectStatus.PROCESSING]: { [Locale.ZH_CN]: '制作中', [Locale.EN_US]: 'Processing' },
  [ProjectStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
  [ProjectStatus.ARCHIVED]: { [Locale.ZH_CN]: '已归档', [Locale.EN_US]: 'Archived' },
};

export enum VideoStyle {
  REALISTIC = 'realistic',
  CARTOON = 'cartoon',
  ANIME = 'anime',
  DOCUMENTARY = 'documentary',
  CINEMATIC = 'cinematic',
  VINTAGE = 'vintage',
  MODERN = 'modern'
}

export const VideoStyleLabels: I18nLabels<VideoStyle> = {
  [VideoStyle.REALISTIC]: { [Locale.ZH_CN]: '写实风格', [Locale.EN_US]: 'Realistic' },
  [VideoStyle.CARTOON]: { [Locale.ZH_CN]: '卡通风格', [Locale.EN_US]: 'Cartoon' },
  [VideoStyle.ANIME]: { [Locale.ZH_CN]: '动漫风格', [Locale.EN_US]: 'Anime' },
  [VideoStyle.DOCUMENTARY]: { [Locale.ZH_CN]: '纪录片风格', [Locale.EN_US]: 'Documentary' },
  [VideoStyle.CINEMATIC]: { [Locale.ZH_CN]: '电影风格', [Locale.EN_US]: 'Cinematic' },
  [VideoStyle.VINTAGE]: { [Locale.ZH_CN]: '复古风格', [Locale.EN_US]: 'Vintage' },
  [VideoStyle.MODERN]: { [Locale.ZH_CN]: '现代风格', [Locale.EN_US]: 'Modern' },
};

export interface IProject extends BaseModelAttributes {
  name: string;
  description: string;
  thumbnailUrl?: string;
  userId: string;
  status: ProjectStatus;
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: string;
  // 自动生成相关字段
  /** 自动生成的原始创意 */
  autoGenScriptIdea?: string;
  /** 是否正在自动生成 */
  autoGenIsActive?: boolean;
  /** 当前生成步骤 */
  autoGenCurrentStep?: GenerationStepType | null;
  /** 已完成的步骤列表 */
  autoGenCompletedSteps?: GenerationStepType[];
  /** 失败的步骤 */
  autoGenFailedStep?: GenerationStepType | null;
  /** 错误信息 */
  autoGenErrorMessage?: string;
  // 小说上传相关字段
  /** 小说文件 OSS URL */
  novelFileUrl?: string;
  /** 小说总章节数 */
  novelTotalChapters?: number;
  /** 小说总字数 */
  novelTotalCharCount?: number;
  /** 单个分镜最大时长（秒），如 2.5/5/7.5/10/12.5/15，NULL 表示不限制 */
  maxShotDuration?: number;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  episodes?: {
    id: string;
    title: string;
    status: string;
  }[];
}

export interface CreateProjectRequest {
  name: string;
  description: string;
  thumbnailUrl?: string;
  status?: ProjectStatus;
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: VideoStyle;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  thumbnailUrl?: string;
  status?: ProjectStatus;
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: VideoStyle;
}

export interface ProjectQuery {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
  status?: ProjectStatus;
}
